import { useState, useEffect } from 'react'
import { Newspaper, Clock, RefreshCw, ChevronRight, Globe } from 'lucide-react'

interface NewsItem {
  title: string
  description: string
  url: string
  publishedAt: string
  source: string
  category: string
}

// 模拟新闻数据（带时间戳，每次刷新会更新）
const generateMockNews = (): NewsItem[] => {
  const categories = ['科技', '财经', '国际', '体育', '娱乐']
  const sources = ['BBC', 'Reuters', 'CNN', 'TechCrunch', 'Bloomberg', 'ESPN', '环球时报']
  
  const newsTemplates = [
    {
      title: '全球科技巨头发布最新AI产品，引发行业变革',
      desc: '多家科技公司今日宣布推出新一代人工智能系统，预计将深刻改变人们的工作和生活方式...',
    },
    {
      title: '国际股市大幅波动，投资者关注全球经济形势',
      desc: '今日全球主要股市出现显著波动，分析师认为这与最新经济数据和地缘政治因素有关...',
    },
    {
      title: '新型清洁能源技术取得重大突破',
      desc: '科学家团队在可再生能源领域取得重大进展，新技术有望大幅降低清洁能源成本...',
    },
    {
      title: '世界杯预选赛：多支强队晋级形势明朗',
      desc: '今日进行的世界杯预选赛中，多支传统强队表现出色，基本锁定出线名额...',
    },
    {
      title: '好莱坞新片创票房纪录，全球影迷热议',
      desc: '备受期待的大片今日全球上映，首日票房打破多项纪录，引发影迷热议...',
    },
    {
      title: '全球气候会议达成重要共识',
      desc: '来自190多个国家的代表在气候峰会上达成历史性协议，承诺加速减排行动...',
    },
    {
      title: '加密货币市场强劲反弹，比特币突破新高',
      desc: '数字货币市场今日迎来强劲反弹，比特币价格创下近月新高，以太坊紧随其后...',
    },
    {
      title: '新型疫苗研发取得阶段性成果',
      desc: '医疗研究团队宣布在新型疫苗研发上取得重要突破，临床试验显示良好效果...',
    },
  ]
  
  return newsTemplates.map((news, index) => ({
    title: news.title,
    description: news.desc,
    url: '#',
    publishedAt: new Date(Date.now() - index * 3600000).toISOString(), // 每个新闻间隔1小时
    source: sources[Math.floor(Math.random() * sources.length)],
    category: categories[Math.floor(Math.random() * categories.length)],
  }))
}

// 格式化发布时间
const formatTime = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 7) return `${days}天前`
  
  return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
}

// 分类颜色
const getCategoryColor = (category: string) => {
  const colors: { [key: string]: string } = {
    '科技': 'bg-blue-500/20 text-blue-400',
    '财经': 'bg-green-500/20 text-green-400',
    '国际': 'bg-purple-500/20 text-purple-400',
    '体育': 'bg-orange-500/20 text-orange-400',
    '娱乐': 'bg-pink-500/20 text-pink-400',
  }
  return colors[category] || 'bg-gray-500/20 text-gray-400'
}

export default function NewsWidget() {
  const [news, setNews] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState('全部')
  const [displayCount, setDisplayCount] = useState(4)

  const categories = ['全部', '科技', '财经', '国际', '体育', '娱乐']

  const fetchNews = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // 模拟API延迟
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // 生成模拟数据
      const mockData = generateMockNews()
      setNews(mockData)
    } catch (err) {
      setError('获取新闻失败')
      console.error('News fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNews()
    
    // 每5分钟自动刷新
    const interval = setInterval(fetchNews, 300000)
    return () => clearInterval(interval)
  }, [])

  const filteredNews = selectedCategory === '全部' 
    ? news 
    : news.filter(item => item.category === selectedCategory)

  const displayNews = filteredNews.slice(0, displayCount)

  return (
    <div className="bg-telegram-dark rounded-xl overflow-hidden">
      {/* 标题栏 */}
      <div className="px-4 py-3 border-b border-telegram-secondary/10 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-telegram-text flex items-center">
          <Newspaper className="w-5 h-5 mr-2 text-blue-400" />
          全球资讯
        </h3>
        <button
          onClick={fetchNews}
          className="p-1.5 rounded-lg hover:bg-telegram-secondary/20 transition-colors"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 text-telegram-secondary ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* 分类筛选 */}
      <div className="px-4 py-2 border-b border-telegram-secondary/10 overflow-x-auto">
        <div className="flex space-x-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${
                selectedCategory === category
                  ? 'bg-telegram-blue text-white'
                  : 'bg-telegram-secondary/20 text-telegram-secondary hover:bg-telegram-secondary/30'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* 新闻列表 */}
      <div className="divide-y divide-telegram-secondary/10">
        {loading && news.length === 0 ? (
          // 加载骨架屏
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="px-4 py-3 animate-pulse">
              <div className="flex items-start space-x-3">
                <div className="w-16 h-16 bg-telegram-secondary/20 rounded-lg flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-telegram-secondary/20 rounded w-3/4" />
                  <div className="h-3 bg-telegram-secondary/20 rounded w-full" />
                  <div className="h-3 bg-telegram-secondary/20 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))
        ) : error ? (
          <div className="px-4 py-6 text-center">
            <p className="text-telegram-secondary text-sm">{error}</p>
            <button
              onClick={fetchNews}
              className="mt-3 px-4 py-2 bg-telegram-blue/20 text-telegram-blue rounded-lg text-sm"
            >
              重新加载
            </button>
          </div>
        ) : (
          displayNews.map((item, index) => (
            <div
              key={index}
              className="px-4 py-3 hover:bg-telegram-secondary/10 transition-colors cursor-pointer group"
              onClick={() => {
                // 实际使用时可以打开新闻详情或外部链接
                window.open(item.url, '_blank')
              }}
            >
              <div className="flex items-start space-x-3">
                {/* 新闻缩略图占位 */}
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex-shrink-0 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-telegram-secondary/50" />
                </div>
                
                {/* 新闻内容 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs ${getCategoryColor(item.category)}`}>
                      {item.category}
                    </span>
                    <span className="text-xs text-telegram-secondary">{item.source}</span>
                  </div>
                  
                  <h4 className="text-sm font-medium text-telegram-text leading-snug mb-1 group-hover:text-telegram-blue transition-colors line-clamp-2">
                    {item.title}
                  </h4>
                  
                  <p className="text-xs text-telegram-secondary line-clamp-2 mb-2">
                    {item.description}
                  </p>
                  
                  <div className="flex items-center text-xs text-telegram-secondary/70">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatTime(item.publishedAt)}
                  </div>
                </div>
                
                {/* 箭头图标 */}
                <ChevronRight className="w-5 h-5 text-telegram-secondary/50 flex-shrink-0 group-hover:text-telegram-blue transition-colors" />
              </div>
            </div>
          ))
        )}
      </div>

      {/* 加载更多 */}
      {!loading && filteredNews.length > displayCount && (
        <button
          onClick={() => setDisplayCount(prev => prev + 3)}
          className="w-full py-3 text-sm text-telegram-secondary hover:bg-telegram-secondary/10 transition-colors border-t border-telegram-secondary/10"
        >
          加载更多
        </button>
      )}

      {/* API 配置提示 */}
      <div className="px-4 py-2 bg-telegram-secondary/5 border-t border-telegram-secondary/10">
        <p className="text-xs text-telegram-secondary/60 text-center">
          💡 提示：集成真实新闻API可获得实时资讯
        </p>
      </div>
    </div>
  )
}
